export interface FbSqUser {
  uid: string;
  email: string;
  name: string;
  isAnonymous: boolean;
  emailVerified: boolean;
  isEditor: boolean;
  createdOn: number;
  modifiedOn: number;
}

// #region USER / PLAYER TYPES ************************************************
export type SqUserID = string;
export type SqUser = {
  id: SqUserID;
  name: string;
};

export type SqPlayerMarker = {
  markerIndex: number;
  markerValue: string;
};

export type SqPlayerPick = {
  playerID: SqUserID;
  sqGameID: SqGameID;
  pick: number;
  marker: SqPlayerMarker;
};

export type SqPlayer = {
  numberOfPicks: number;
  picks: number[];
  winningQuarters: number[];
  marker: SqPlayerMarker;
  isAdmin: boolean;
  createdOn: number;
  gameId: SqGameID;
  isBanned: boolean;
} & SqUser;
// #endregion

// #region SQ GAME TYPES ******************************************************
export type SqGameID = string;
export type SqPickKind = string | SqPlayerPick;
export type SqPicksKind = Array<SqPickKind>;

export type SqTeam = {
  id: string;
  name: string;
  logoUrl: string;
  randomNumbers: number[];
  pointsScored: number;
  pointsAllowed: number;
  displayPosition: 'vertical' | 'horizontal';
};

// #region SCORING BREAKDOWN **************************************************
export type SqGameScore = {
  sqGameId: string;
  home: number;
  away: number;
  quarter: number;
};

export enum SqScoringOption {
  TD = 'TD (6pts)',
  FG = 'FG (3pts)',
  SF = 'Safety (2pts)',
  PAT = 'PAT (1pt)',
  PAT2 = '2PAT (2pts)',
  NONE = 'NO SCORING',
}

export type SqTeamScoringDetails = {
  pointsNeeded: number;
  scoreNeeded: number;
  scoringOptions: SqScoringOption[];
};

export type SqScoringBreakdown = {
  cellIndex: number;
  currentScore: {
    homeTeamScore: number;
    awayTeamScore: number;
  };
  quarter: number;
  withSafety: SqTeamScoringDetails[];
  withoutSafety: SqTeamScoringDetails[];
};
// #endregion

export enum SqGameProgress {
  NotStarted = 0,
  Qtr1 = 1,
  Qtr2 = 2,
  Qtr3 = 3,
  Qtr4 = 4,
  Overtime = 5,
  Over = 6,
}

export enum SqGameState {
  Picks = 'Picks',
  GameCountdown = 'GameCountdown',
  GameReady = 'GameReady',
  GameOn = 'GameOn',
  GameComplete = 'GameComplete',
}

export type QuarterWinner = {
  winningIndex: number;
  winningQtr: number;
} & SqGameScore &
  SqPlayer;

export type SqGame = {
  /** `SqGameID` unique id identifying this game */
  sqGameId: SqGameID;
  /** Name of the game */
  title: string;
  description?: string;
  /** link to this game */
  url?: string;
  /** `id`s of all the players who have joined this game */
  playerIds: SqUserID[];
  /** `number` current number of unclaimed (open) squares in this game */
  openSquares: number;
  /** `SqTeam` home team of the actual NFL match this game is using as the scoring input  */
  homeTeam: SqTeam;
  /** `SqTeam` away team of the actual NFL match this game is using as the scoring input  */
  awayTeam: SqTeam;
  /** `string` id of the actual NFL match this game is using as the scoring input  */
  matchId?: string;
  /** `SqGameProgress` enum representing the current progress of the game,
   * 0 - not started, 1-4 quarters, 5 - overtime, and 6 - completed */
  gameProgress: SqGameProgress;
  /** `SqGameState` enum where each state is the action that the game is performing
   * at that moment of the game. */
  gameState: SqGameState;
  /** `boolean` indicates the locked state of the board
   * @description Once all the squares have been filled, and the countdown timer reaches zero,
   * the game locks the board to prevent any future changes made to the picks made earlier */
  boardLocked: boolean;
  /** Player who created the game, admins get a few additional privileges such as
   * banning player and setting the max picks each player, including the admin, can make. */
  gameAdmin: SqUser;
  /** `number` representing the time real NFL match begins */
  startingDate: number;
  /** `Array<SqGameScore>` each time score of the actual NFL match
   * changes we record the change and add it to this `Array` */
  quarterScores: SqGameScore[];
  quarterWinners: QuarterWinner[];
  bannedPlayerIds: string[];
  /** `number` indicates the max squares user can choose to pick */
  maxPicksPerPlayer: number;

  /** the usual suspects */
  createdBy: string;
  createdOn: number;

  /** @unknown not sure */
  date: string;
  /** @deprecated */
  pickingPlayer: SqUserID | '';
  /** @deprecated */
  lastPickingPlayer: SqUserID | '';

  /** BELOW ARE FE PROPS ONLY ************************************************/

  /** Omitted from `SqGameBackEndModel`. @see SqGameBackEndModel */
  picks: SqPicksKind;
  /** Omitted from `SqGameBackEndModel`. @see SqGameBackEndModel */
  players: SqPlayer[];
};

export type SqGameBackEndModel = Omit<SqGame, 'players' | 'picks'>;

// #endregion

// #region SLICE TYPES ********************************************************
export enum TabPanel {
  None = 'None',
  Scoreboard = 'Scoreboard',
  Login = 'Login',
  Select = 'Select',
  Settings = 'Settings',
}

export type TabType = keyof typeof TabPanel;

export type SquaresProps = {
  [key: string]: unknown;
  isLoading: boolean;
  games: SqGameBackEndModel[];
  resumeGames: SqGameBackEndModel[];
  completedGames: SqGameBackEndModel[];
  activeSqPlayerId: SqUserID;
  activeSqGameId: string;
};
// #endregion

// #region REACT ROUTER PAYLOADS **********************************************
export enum SqGamePayloadActionType {
  CreateGame = 'CreateGame',
  JoinGame = 'JoinGame',
  ResumeGame = 'ResumeGame',
  // TODO: Consider LeaveGame, BannedFromGame,
}

export type SqGamePayloadAction = {
  type: SqGamePayloadActionType;
  gameId: SqGameID;
  gameName: string;
  matchId: string;
  redirectUrl: string;
  playerId: SqUserID;
  playerName: string;
};
// #endregion

// #endregion
